
import React from 'react'; // https://github.com/parcel-bundler/parcel/issues/1199
import {
    FormattedHTMLMessage,
    InjectedIntlProps,
    injectIntl,
} from 'react-intl';
import {
    FormFieldComponentProps,
    FormSelectChoice,
    logger,
    InputSelectOnKeyDown,
    setFocus,
    setRef,
    InputSelectComponent,
} from '@sheerid/jslib';

const GraduationDateComponent = ({
    onChange,
    intl,
    isErrored,
    errorId,
    value,
}: FormFieldComponentProps & InjectedIntlProps) => {
    const graduationDateParts = value.split('/');
    const graduationDateMonth = Number.parseInt(graduationDateParts[0], 10) || undefined;
    const graduationDateYear = Number.parseInt(graduationDateParts[1], 10) || undefined;

    const thisYear = new Date().getFullYear();

    const months: FormSelectChoice[] = [
        { value: '1', label: intl.formatMessage({ id: 'dateTime.january', defaultMessage: 'January' }) },
        { value: '2', label: intl.formatMessage({ id: 'dateTime.february', defaultMessage: 'February' }) },
        { value: '3', label: intl.formatMessage({ id: 'dateTime.march', defaultMessage: 'March' }) },
        { value: '4', label: intl.formatMessage({ id: 'dateTime.april', defaultMessage: 'April' }) },
        { value: '5', label: intl.formatMessage({ id: 'dateTime.may', defaultMessage: 'May' }) },
        { value: '6', label: intl.formatMessage({ id: 'dateTime.june', defaultMessage: 'June' }) },
        { value: '7', label: intl.formatMessage({ id: 'dateTime.july', defaultMessage: 'July' }) },
        { value: '8', label: intl.formatMessage({ id: 'dateTime.august', defaultMessage: 'August' }) },
        { value: '9', label: intl.formatMessage({ id: 'dateTime.september', defaultMessage: 'September' }) },
        { value: '10', label: intl.formatMessage({ id: 'dateTime.october', defaultMessage: 'October' }) },
        { value: '11', label: intl.formatMessage({ id: 'dateTime.november', defaultMessage: 'November' }) },
        { value: '12', label: intl.formatMessage({ id: 'dateTime.december', defaultMessage: 'December' }) },
    ];

    const updateGraduationMonth = (month: string) => {
        if (month === '' || month === null) {
            onChange(`/${graduationDateYear}`);
        }

        const newVal = Number.parseInt(month, 10);
        if (typeof newVal === 'number' && newVal >= 1 && newVal <= 12) {
            onChange(`${month}/${graduationDateYear}`);
        } else {
            logger.warn(`Value '${newVal}' is an invalid graduation month`);
        }
    };

    const updateGraduationYear = (year: string) => {
        if (year === '') {
            onChange(`${graduationDateMonth}/`);
        }
        const newVal = Number.parseInt(year, 10);
        if (typeof newVal === 'number' && newVal >= 1 && newVal <= thisYear + 11) {
            onChange(`${graduationDateMonth}/${newVal}`);
        } else {
            logger.warn(`Value '${newVal}' is an invalid graduation year`);
        }
    };

    const getGraduationDate = () => {
        onChange(`${graduationDateMonth}/${graduationDateYear}`);
    };

    const handleOnKeyDown: InputSelectOnKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
        if (event.key === 'Tab' && !event.shiftKey) {
            event.preventDefault();
            setFocus('graduationDateYear');
        }
    };

    return (
        <div className="sid-field sid-graduationdate">
            <div className="sid-l-space-top-md" />
            <label htmlFor="sid-graduationdate__month">
                <div className="sid-field__label-with-explanation sid-l-space-btm-sm">
                    <div className="sid-field__label sid-field__label--required">
                        <FormattedHTMLMessage
                            id="graduationDate"
                            defaultMessage="Anticipated Graduation Date"
                        />
                    </div>
                    <div className="sid-field__label-explanation">
                        <FormattedHTMLMessage
                            id="verificationPurposesOnly"
                            defaultMessage="Used for verification purposes only"
                        />
                    </div>
                </div>
            </label>

            <div className="sid-graduationdate__inputs">
                <InputSelectComponent
                    className="sid-graduationdate__month"
                    fieldId="graduationDate"
                    inputId="sid-graduationdate__month"
                    isErrored={isErrored}
                    options={months}
                    onKeyDown={event => handleOnKeyDown(event)}
                    onChange={
                        (choice: FormSelectChoice) => {
                            if (choice) {
                                updateGraduationMonth(choice.value.toString());
                                setFocus('graduationDateYear');
                            } else {
                                // clear the graduation month
                                updateGraduationMonth('');
                            }
                        }
                    }
                    placeholder={intl.formatMessage({ id: 'dateTime.month', defaultMessage: 'Month' })}
                    value={graduationDateMonth ? months[graduationDateMonth - 1] : undefined}
                />

                <div className="sid-graduation-date__slash">
                    <FormattedHTMLMessage
                        id="graduationDateSlash"
                        defaultMessage=" / "
                    />
                </div>

                <input
                    className={`sid-graduationdate__year sid-text-input sid-text-input--required sid-hidden-placeholder ${isErrored ? 'sid-text-input--error' : ''}`}
                    id="sid-graduationdate-year"
                    name="sid-graduationdate-year"
                    type="text"
                    min={thisYear}
                    max={thisYear + 11}
                    value={graduationDateYear || ''}
                    placeholder={intl.formatMessage({ id: 'dateTime.year', defaultMessage: 'Year' })}
                    onChange={e => updateGraduationYear(e.target.value)}
                    onBlur={getGraduationDate}
                    ref={input => setRef('graduationDateYear', input)}
                />
            </div>

            {
                isErrored
                    ? (
                        <div className="sid-field-error">
                            <FormattedHTMLMessage
                                id={`errorId.${errorId || 'invalidGraduationDate'}`}
                                defaultMessage="Invalid graduation date"
                            />
                        </div>
                    ) : null
            }
        </div>
    );
};

GraduationDateComponent.defaultProps = {
    value: `1/${new Date().getFullYear()}`,
};

export const GraduationDate = injectIntl(GraduationDateComponent);
